import React from 'react';
import { CheckCircle, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { portfolioResults, contactInfo } from '../data/mock';

const Results = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0D234E] to-[#1a3a6e] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Proven Results
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Real businesses. Real growth. Real ROI. See how we've helped our clients achieve remarkable success through strategic digital marketing.
            </p>
            <Link to="/salesproof" data-testid="results-salesproof-link" className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#BFB47E] px-6 py-3 font-semibold text-[#0D234E] transition-colors hover:bg-[#d4c991]">
              View Sales Proof <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {portfolioResults.map((project, index) => (
              <div key={project.id}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="inline-block bg-[#BFB47E] bg-opacity-20 px-4 py-2 rounded-full mb-4">
                      <span className="text-[#0D234E] font-semibold text-sm">{project.category}</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0D234E] mb-4">
                      {project.title}
                    </h2>
                    
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-[#0D234E] mb-2">Challenge:</h3>
                      <p className="text-gray-700 leading-relaxed">{project.challenge}</p>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-[#0D234E] mb-2">Solution:</h3>
                      <p className="text-gray-700 leading-relaxed">{project.solution}</p>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-[#0D234E] mb-3">Results:</h3>
                      <div className="space-y-3">
                        {project.results.map((result, i) => (
                          <div key={i} className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <TrendingUp className="text-[#BFB47E]" size={20} />
                            </div>
                            <span className="text-gray-700 font-medium">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 mb-6">
                      <strong>Timeline:</strong> {project.duration}
                    </div>
                  </div>

                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#BFB47E] rounded-2xl transform rotate-3 opacity-20"></div>
                      <img
                        src={project.image}
                        alt={project.title}
                        className="relative z-10 rounded-2xl shadow-xl w-full h-[450px] object-cover"
                      />
                    </div>
                  </div>
                </div>

                {index < portfolioResults.length - 1 && (
                  <div className="mt-20 border-b border-gray-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D234E] mb-4">
              Our Track Record
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Numbers that demonstrate our commitment to client success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '250%', label: 'Average Revenue Growth' },
              { value: '400%', label: 'Traffic Increase' },
              { value: '4.5x', label: 'Average ROI' },
              { value: '98%', label: 'Client Satisfaction' }
            ].map((stat, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md text-center">
                <div className="text-5xl font-bold text-[#0D234E] mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D234E] mb-4">
              Industries We've Transformed
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From e-commerce to B2B services, we deliver results across diverse sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'E-commerce',
                description: 'Helping online stores increase sales and customer lifetime value',
                icon: '🛍️'
              },
              {
                title: 'Authors & Creators',
                description: 'Building personal brands and engaged communities',
                icon: '✍️'
              },
              {
                title: 'B2B Services',
                description: 'Generating qualified leads and shortening sales cycles',
                icon: '💼'
              },
              {
                title: 'Tech Startups',
                description: 'Scaling user acquisition and establishing market presence',
                icon: '🚀'
              },
              {
                title: 'Local Businesses',
                description: 'Dominating local search and driving foot traffic',
                icon: '🏪'
              },
              {
                title: 'Healthcare',
                description: 'Building trust and attracting new patients online',
                icon: '⚕️'
              }
            ].map((industry, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow duration-300"
              >
                <div className="text-4xl mb-4">{industry.icon}</div>
                <h3 className="text-xl font-bold text-[#0D234E] mb-3">
                  {industry.title}
                </h3>
                <p className="text-gray-600">
                  {industry.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0D234E] to-[#1a3a6e] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Let's create results that matter for your business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#BFB47E] text-[#0D234E] px-8 py-4 rounded-md font-semibold hover:bg-[#d4c991] transition-all duration-200 hover:shadow-xl inline-flex items-center justify-center"
            >
              Get Your Free Strategy Call
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <a
              href={contactInfo.whatsapp}
              data-testid="results-contact-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-md font-semibold hover:bg-white hover:text-[#0D234E] transition-all duration-200 inline-flex items-center justify-center"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Results;
